import React, { useMemo } from 'react';
import { useScopedI18n } from '@locales/client';

import { Box } from '@/shared/ui/box';
import TrashIcon from '@/shared/assets/trash-icon.svg';
import { Condition } from '@/shared/ui/condition';
import {
  BodyTextBoldSecondary,
  BodyTextBoldTertiary,
  BodyTextRegularSecondary,
} from '@/shared/ui/typography';

import { AddressBlock } from '../withdraw-form/styles';
import {
  useWithdrawForm,
  useAddressSelection,
} from '@/features/wallet-managment/model/hooks';
import {
  parseAddress,
  getCardFieldCrypt,
} from '@/features/wallet-managment/model/utils';
import { Operations } from '../address-detail/styles';
import type { AdressesDetailProps } from './address-detail-fiat';

export const AdressesDetailCrypt = ({
  keyAddres,
  onEdit,
  inputValue,
}: AdressesDetailProps) => {
  const t = useScopedI18n('wallet');

  const { savedAdresses, deleteUserAdressesTrigger, mutateSavedAdresses } =
    useWithdrawForm(null);

  const parsedAddress = useMemo(
    () => parseAddress(savedAdresses, keyAddres),
    [savedAdresses, keyAddres]
  );

  const {
    selectedAddress,
    blinkingAddress,
    setSelectedAddress,
    setBlinkingAddress,
  } = useAddressSelection({
    inputValue: inputValue?.address || '',
    savedAddresses: parsedAddress ? [parsedAddress] : [],
    getAddress: (item) => item?.address?.address || '',
    onEdit: (item) => onEdit(item?.address),
  });

  const handleDeleteAddress = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    await deleteUserAdressesTrigger({ id });
    await mutateSavedAdresses();

    if (selectedAddress === inputValue?.address) {
      setSelectedAddress(null);
      onEdit(null);
    }
  };

  if (!savedAdresses?.length) return null;

  const addressValue = parsedAddress?.address?.address || '';
  const isSelected = selectedAddress === addressValue;
  const isBlinking = blinkingAddress === addressValue;

  const handleClickAddress = () => {
    setSelectedAddress(addressValue);
    setBlinkingAddress(null);
    onEdit(parsedAddress.address);
  };

  return (
    <Condition match={!!parsedAddress?.id}>
      <BodyTextBoldSecondary color="#AFAFAF">
        {t('withdraw.save_address.title')}
      </BodyTextBoldSecondary>
      <AddressBlock
        $border={isSelected}
        $blinking={isBlinking}
        onClick={handleClickAddress}
      >
        <Box
          $display="flex"
          $alignItems="center"
          $justifyContent="space-between"
        >
          <Box>
            <BodyTextRegularSecondary color="#AFAFAF">
              {t('deposit.method')}
            </BodyTextRegularSecondary>
            <BodyTextBoldTertiary>
              {getCardFieldCrypt(parsedAddress)}
            </BodyTextBoldTertiary>
          </Box>
          <Operations $display="flex" $gap={16} $alignItems="center">
            <TrashIcon
              onClick={(e) => handleDeleteAddress(e, parsedAddress.id)}
            />
          </Operations>
        </Box>
      </AddressBlock>
    </Condition>
  );
};
